import {message} from 'antd';

/**
 * Toast wrapper utility that abstracts the underlying toast library.
 * Currently uses antd message, but can be easily swapped for react-hot-toast,
 * sonner, or any other toast library by changing this file.
 */

/**
 * Internal implementation - wraps antd message
 * To swap libraries, only modify this object
 */
const toastImpl = {
  loading: (content, key) => message.loading({content, key}),
  success: (content, key) => message.success({content, key}),
  error: (content, key) => message.error({content, key}),
  info: (content, key) => message.info({content, key}),
  warning: (content, key) => message.warning({content, key}),
  destroy: (key) => message.destroy(key),
};

/**
 * Handles async operations with automatic loading/success/error toast messages.
 *
 * @param {Promise} operation - The async operation to execute
 * @param {Object} messages - Toast messages configuration
 * @param {string} messages.loading - Message to show while loading
 * @param {string} messages.success - Message to show on success
 * @param {string|Function} [messages.error] - Message to show on error (string or function that receives error)
 * @param {string} [messages.key] - Optional key for the toast (defaults to generated key)
 * @returns {Promise} - Returns the original promise result
 *
 * @example
 * asyncToast(
 *   savePage(data).unwrap(),
 *   {
 *     loading: 'Saving...',
 *     success: 'Page saved successfully',
 *     error: 'Failed to save page',
 *     key: 'save'
 *   }
 * ).then((result) => { ... });
 *
 * @example
 * // With dynamic error message
 * asyncToast(
 *   deletePage(id).unwrap(),
 *   {
 *     loading: 'Deleting...',
 *     success: 'Deleted successfully',
 *     error: (err) => err.message || 'Failed to delete',
 *     key: 'delete'
 *   }
 * );
 */
export const asyncToast = async (operation, messages) => {
  const {loading, success, error, key = `toast-${Date.now()}`} = messages;

  if (loading) {
    toastImpl.loading(loading, key);
  }

  try {
    const result = await operation;
    if (success) {
      toastImpl.success(success, key);
    }
    return result;
  } catch (err) {
    const errorMessage =
      typeof error === 'function' ? error(err) : error || 'Operation failed';
    toastImpl.error(errorMessage, key);
    throw err;
  }
};

/**
 * Show a success toast
 * @param {string} content - Message content
 * @param {string} [key] - Optional key for the toast
 */
export const toast = {
  success: (content, key) => toastImpl.success(content, key),
  error: (content, key) => toastImpl.error(content, key),
  info: (content, key) => toastImpl.info(content, key),
  warning: (content, key) => toastImpl.warning(content, key),
  loading: (content, key) => toastImpl.loading(content, key),
  destroy: (key) => toastImpl.destroy(key),
};

/**
 * Export the underlying message API for complex use cases
 * (e.g., error handler with custom JSX content)
 * When migrating to a new library, this would need updates
 */
export const messageApi = message;
